import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { SCORE_IDS, TONE } from '../../data/constants'
import {
  getAddOnCoveragePct,
  getEligibleCoveragePct,
  getGuestAccessScore,
  getNavScoreValue,
  getNavTone,
} from '../../data/reportModel'
import { getTenantAuthzScore } from '../../data/tenantAuthzPolicy'
import { useAIRiskReportExportMode } from '../../components/RenderMode/AIRiskReportRenderModeProvider'
import ScoreProgress from '../../components/ScoreProgress/ScoreProgress'
import styles from './SectionNavCards.module.scss'

const buildRows = ({ summary, domainScores, data, dataAvailability }) => ({
  deployment: [
    { labelId: 'AI_RISK_REPORT.DEPLOYMENT.ELIGIBLE.TITLE', value: getEligibleCoveragePct(summary) },
    { labelId: 'AI_RISK_REPORT.DEPLOYMENT.ADDON.TITLE', value: getAddOnCoveragePct(summary) },
    { labelId: 'AI_RISK_REPORT.DEPLOYMENT.COPILOT_CONFIG.TITLE', comingSoon: true },
  ],
  data: [
    {
      labelId: 'AI_RISK_REPORT.DATA.SHAREPOINT.TITLE',
      value: getNavScoreValue(domainScores, SCORE_IDS.COPILOT_DATA_EXPOSURE),
    },
    { labelId: 'AI_RISK_REPORT.DATA.LABELS.TITLE', value: getNavScoreValue(domainScores, SCORE_IDS.DATA_GOVERNANCE) },
    { labelId: 'AI_RISK_REPORT.DATA.GROUPS.TITLE', value: getNavScoreValue(domainScores, SCORE_IDS.GROUP_GOVERNANCE) },
    {
      labelId: 'AI_RISK_REPORT.DATA.GUESTS.TITLE',
      value: getGuestAccessScore(summary, data.guestUsers ?? [], dataAvailability),
    },
  ],
  identity: [
    {
      labelId: 'AI_RISK_REPORT.IDENTITY.ADMINS.TITLE',
      value: getNavScoreValue(domainScores, SCORE_IDS.IDENTITY_ACCESS),
    },
    {
      labelId: 'AI_RISK_REPORT.IDENTITY.CA.TITLE',
      value: getNavScoreValue(domainScores, SCORE_IDS.CONDITIONAL_ACCESS),
    },
    { labelId: 'AI_RISK_REPORT.IDENTITY.MFA.TITLE', value: summary.users?.mfa?.registeredPct ?? null },
    { labelId: 'AI_RISK_REPORT.IDENTITY.DEVICE.TITLE', comingSoon: true },
  ],
  thirdParty: [
    { labelId: 'AI_RISK_REPORT.THIRD.SHADOW_AI.TITLE', comingSoon: true },
    { labelId: 'AI_RISK_REPORT.THIRD.APPS_AND_SPS.TITLE', value: getNavScoreValue(domainScores, SCORE_IDS.APPS_OAUTH) },
    { labelId: 'AI_RISK_REPORT.THIRD.AUTHZ.TITLE', value: getTenantAuthzScore(data.tenant) },
  ],
})

function CardRow({ row }) {
  const hasValue = !row.comingSoon && Number.isFinite(row.value)
  const tone = hasValue ? getNavTone(row.value) : TONE.NEUTRAL

  return (
    <div className={`${styles.row} d-grid column-gap-2 row-gap-1 align-items-baseline`}>
      <span className={styles.label}>
        <FormattedMessage id={row.labelId} />
      </span>
      {hasValue && <span className={`${styles.value} ${styles[tone] ?? ''}`}>{row.value}%</span>}
      <span className={styles.progressCell}>
        <ScoreProgress
          value={row.value}
          tone={tone}
          showValue={false}
          reserveValueSpace={false}
          size="compact"
          disabled={!hasValue}
        />
      </span>
    </div>
  )
}

CardRow.propTypes = {
  row: PropTypes.shape({ labelId: PropTypes.string.isRequired, value: PropTypes.number, comingSoon: PropTypes.bool })
    .isRequired,
}

function NavCard({ anchor, titleId, rows }) {
  const isExportMode = useAIRiskReportExportMode()
  const content = (
    <>
      <div className={styles.title}>
        <FormattedMessage id={titleId} />
      </div>
      <div className="d-flex flex-column gap-2">
        {rows.map((row) => (
          <CardRow key={row.labelId} row={row} />
        ))}
      </div>
      {!isExportMode && (
        <span className={`${styles.jump} mt-auto`}>
          <FormattedMessage id="AI_RISK_REPORT.VIEW_DETAILS" /> ↓
        </span>
      )}
    </>
  )

  if (isExportMode) {
    return <div className={`${styles.card} d-flex flex-column`}>{content}</div>
  }

  return (
    <a href={`#${anchor}`} className={`${styles.card} d-flex flex-column`}>
      {content}
    </a>
  )
}

NavCard.propTypes = {
  anchor: PropTypes.string.isRequired,
  titleId: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
}

export default function SectionNavCards({ summary = {}, domainScores = [], data = {}, dataAvailability = [] }) {
  const rows = buildRows({ summary, domainScores, data, dataAvailability })

  return (
    <div className={`${styles.grid} d-grid`}>
      <NavCard anchor="deployment" titleId="AI_RISK_REPORT.SECTION.DEPLOYMENT.TITLE" rows={rows.deployment} />
      <NavCard anchor="data-exposure" titleId="AI_RISK_REPORT.SECTION.DATA_EXPOSURE.TITLE" rows={rows.data} />
      <NavCard anchor="identity-access" titleId="AI_RISK_REPORT.SECTION.IDENTITY.TITLE" rows={rows.identity} />
      <NavCard anchor="third-party-ai" titleId="AI_RISK_REPORT.SECTION.THIRD_PARTY.TITLE" rows={rows.thirdParty} />
    </div>
  )
}

SectionNavCards.propTypes = {
  summary: PropTypes.shape({}),
  domainScores: PropTypes.arrayOf(PropTypes.shape({})),
  data: PropTypes.shape({ guestUsers: PropTypes.arrayOf(PropTypes.shape({})), tenant: PropTypes.shape({}) }),
  dataAvailability: PropTypes.arrayOf(PropTypes.shape({})),
}
